import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Layers, Loader2, Plus, Trash2, UploadCloud, X } from 'lucide-react';
import { useLcs } from '@/hooks/useLcs';
import { persistenceMode } from '@/lib/repo';
import { Button } from '@/components/ui/button';

const DROPZONE_CLASS =
  'mb-2 flex cursor-pointer flex-col items-center justify-center rounded-lg ' +
  'border-2 border-dashed border-border p-8 text-center hover:border-violet-400';

export default function Topics() {
  const { topics, error, refreshTopics, createTopicAndIngest, deleteTopic } = useLcs();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [open, setOpen] = useState(false);
  const [ingest, setIngest] = useState<{ done: number; total: number } | null>(null);

  useEffect(() => {
    void refreshTopics();
  }, [refreshTopics]);

  // The sidebar "New topic" button links to /?new — open the dialog, then clear it.
  useEffect(() => {
    if (params.get('new') !== null) {
      setOpen(true);
      params.delete('new');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  async function onDelete(id: string, title: string) {
    if (!window.confirm(`Delete "${title}"? This removes its sources, atoms, and events, and can't be undone.`)) return;
    await deleteTopic(id);
  }

  async function handleFiles(list: FileList | null) {
    const files = list ? Array.from(list) : [];
    if (files.length === 0) return;
    const rel = (files[0] as File & { webkitRelativePath?: string }).webkitRelativePath;
    const title = rel && rel.includes('/') ? rel.split('/')[0] : `Imported ${files.length} file${files.length === 1 ? '' : 's'}`;
    setIngest({ done: 0, total: files.length });
    const topic = await createTopicAndIngest(title, files, (done, total) => setIngest({ done, total }));
    setIngest(null);
    if (topic) navigate(`/topics/${topic.id}`);
  }

  return (
    <div className="container mx-auto max-w-5xl px-6 py-8">
      {error && <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-600">{error}</div>}

      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold">Recent topics</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Pick up where you left off, or start a new topic.{' '}
            <Link to="/how-it-works" className="text-violet-600 hover:underline">
              How this works →
            </Link>
          </p>
        </div>
        <span className="ml-auto">
          <Button onClick={() => setOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New topic
          </Button>
        </span>
      </div>

      {persistenceMode === 'local' && (
        <p className="mb-4 text-xs text-muted-foreground">
          Running in <strong>local mode</strong> — data is saved in this browser only.
        </p>
      )}

      {/* Recent topics */}
      {topics.length === 0 ? (
        <div className="rounded-lg border bg-card py-16 text-center">
          <Layers className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">No topics yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Create your first topic to start atomizing source material.</p>
          <Button className="mt-4" onClick={() => setOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New topic
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {topics.map((t) => {
            const count = t.sourceCount ?? 0;
            const atomCount = t.atomCount ?? 0;
            return (
              <div
                key={t.id}
                className="group flex items-center rounded-lg border transition-colors hover:border-muted-foreground/50"
              >
                <Link to={`/topics/${t.id}`} className="flex min-w-0 flex-1 items-center gap-3 p-3">
                  <Layers className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{t.title}</span>
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {count} source{count === 1 ? '' : 's'} · {atomCount} atom{atomCount === 1 ? '' : 's'}
                    </span>
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </Link>
                <button
                  onClick={() => void onDelete(t.id, t.title)}
                  title="Delete topic"
                  aria-label={`Delete topic ${t.title}`}
                  className="mr-2 ml-1 shrink-0 rounded p-2 text-muted-foreground transition hover:bg-red-500/10 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* New-topic dialog */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-6 pt-24"
          onClick={() => {
            if (!ingest) setOpen(false);
          }}
        >
          <div className="w-full max-w-xl rounded-lg border bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center">
              <h2 className="text-lg font-semibold">New topic</h2>
              <button className="ml-auto text-muted-foreground hover:text-foreground" onClick={() => !ingest && setOpen(false)}>
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Drag raw files → analyze → mirror to Iconik → atomize */}
            {persistenceMode === 'cloud' ? (
              <>
                <label
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    void handleFiles(e.dataTransfer.files);
                  }}
                  className={DROPZONE_CLASS}
                >
                  {ingest ? (
                    <>
                      <Loader2 className="mb-2 h-6 w-6 animate-spin text-violet-600" />
                      <span className="text-sm font-medium">
                        Analyzing {ingest.done}/{ingest.total}…
                      </span>
                      <span className="mt-0.5 text-xs text-muted-foreground">
                        Transcribing, mirroring to Iconik, then atomizing
                      </span>
                    </>
                  ) : (
                    <>
                      <UploadCloud className="mb-2 h-7 w-7 text-violet-600" />
                      <span className="text-sm font-medium">Drag in video &amp; articles — or click to choose</span>
                      <span className="mt-0.5 text-xs text-muted-foreground">
                        Each file is transcribed / analyzed and landed in Iconik · mp4 · mp3 · pdf · txt · html · images
                      </span>
                    </>
                  )}
                  <input type="file" multiple className="hidden" onChange={(e) => void handleFiles(e.target.files)} />
                </label>
                <p className="text-[11px] text-muted-foreground">
                  Source video is uploaded to Iconik so the Story Engine can pull it — no external references.
                </p>
              </>
            ) : (
              <div className="rounded-lg border-2 border-dashed border-border p-6 text-center text-xs text-muted-foreground">
                Drag-and-drop analysis of video &amp; articles is available once the backend is deployed (cloud mode).
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
